/**
 * PartyCleanupDialog — ménage des fiches après l'import : fusion des doublons probables (même nom à la casse / aux espaces / accents près)
 * et renommage des fiches tout en MAJUSCULES (nom proposé modifiable avant d'appliquer).
 */
import { useState } from "react";
import { GitMerge, Pencil } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Modal, Pill, errMsg, inputCls, money, useGlBase } from "../comptabilite/_shared";
import { PARTY_CFG, partyPayload } from "./_books";

function MergeGroup({ g, cfg, base, onDone }) {
  const [keep, setKeep] = useState(g.suggested_keep);
  const [name, setName] = useState(g.members.find((m) => m.id === g.suggested_keep)?.name || "");
  const [busy, setBusy] = useState(false);
  const others = g.members.filter((m) => m.id !== keep);
  const merge = async () => {
    const nb = others.reduce((s, m) => s + (m.totals?.nb || 0), 0);
    if (!window.confirm(`Fusionner ${others.map((m) => `« ${m.name} »`).join(", ")} dans « ${name.trim()} » ?\n${nb} pièce(s) et leurs paiements passeront à la fiche conservée ; les doublons seront supprimés.`)) return;
    setBusy(true);
    try {
      for (const m of others) await api.post(`${base}/${cfg.path}/${m.id}/merge`, { target_id: keep, name: name.trim() || undefined });
      toast.success(`Fusion effectuée — ${name.trim()}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); onDone(); }
  };
  return (
    <div className="border rounded-xl p-3 space-y-2 bg-white" data-testid={`cleanup-group-${g.key}`}>
      <div className="space-y-1">
        {g.members.map((m) => (
          <label key={m.id} className={`flex items-center gap-2 text-sm cursor-pointer rounded-lg px-2 py-1.5 border ${keep === m.id ? "bg-emerald-50 border-emerald-200" : "border-transparent hover:bg-slate-50"}`} data-testid={`cleanup-member-${m.id}`}>
            <input type="radio" name={`keep-${g.key}`} checked={keep === m.id} onChange={() => { setKeep(m.id); setName(m.name); }} className="accent-emerald-600" data-testid={`cleanup-keep-${m.id}`} />
            {m.number && <span className="font-mono text-xs text-teal-700">{m.number}</span>}
            <span className="font-medium">{m.name}</span>
            {m.is_active === false && <Pill>Inactif</Pill>}
            {keep === m.id && <Pill cls="bg-emerald-100 text-emerald-800 border-emerald-200">Conservée</Pill>}
            <span className="ml-auto text-xs text-slate-500 tabular-nums text-right">{m.totals.nb} pièce(s) · {money(m.totals.total)}{m.totals.balance ? ` · solde ${money(m.totals.balance)}` : ""}</span>
          </label>
        ))}
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] font-semibold text-slate-600 mb-1">Nom final de la fiche conservée</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} maxLength={150} data-testid={`cleanup-name-${g.key}`} />
        </div>
        <Btn icon={GitMerge} onClick={merge} busy={busy} disabled={!name.trim()} testId={`cleanup-merge-${g.key}`}>Fusionner ({others.length})</Btn>
      </div>
    </div>
  );
}

function RenameRow({ p, cfg, base, onDone }) {
  const [name, setName] = useState(p.proposed);
  const [busy, setBusy] = useState(false);
  const rename = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`${base}/${cfg.path}/${p.id}`);
      await api.put(`${base}/${cfg.path}/${p.id}`, partyPayload(data, { name: name.trim() }));
      toast.success(`Renommé : ${name.trim()}`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap bg-white border rounded-xl px-3 py-2" data-testid={`cleanup-upper-${p.id}`}>
      <span className="text-sm font-medium text-slate-700 w-44 truncate" title={p.name}>{p.number && <span className="font-mono text-xs text-teal-700 mr-1">{p.number}</span>}{p.name}</span>
      <span className="text-slate-400">→</span>
      <input value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} flex-1 min-w-[160px]`} maxLength={150} data-testid={`cleanup-upper-name-${p.id}`} />
      <Btn tone="slate" icon={Pencil} onClick={rename} busy={busy} disabled={!name.trim() || name.trim() === p.name} testId={`cleanup-rename-${p.id}`}>Renommer</Btn>
    </div>
  );
}

export default function PartyCleanupDialog({ kind, data, onClose, onChanged }) {
  const base = useGlBase();
  const cfg = PARTY_CFG[kind];
  const groups = data?.groups || [];
  const upper = data?.uppercase || [];
  return (
    <Modal title={`Ménage des ${cfg.plural.toLowerCase()}`} sub="Fusionner les fiches en double (les pièces, notes et paiements passent à la fiche conservée) et corriger les noms importés en majuscules." onClose={onClose} wide testId="party-cleanup-dialog"
      footer={<Btn tone="slate" onClick={onClose} testId="party-cleanup-close">Fermer</Btn>}>
      <section className="space-y-2" data-testid="cleanup-groups">
        <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600">Doublons probables ({groups.length})</h4>
        {groups.length === 0 && <p className="text-xs text-slate-400">Aucun doublon détecté.</p>}
        {groups.map((g) => <MergeGroup key={g.key} g={g} cfg={cfg} base={base} onDone={onChanged} />)}
      </section>
      <section className="space-y-2" data-testid="cleanup-uppercase">
        <h4 className="text-xs font-bold uppercase tracking-wide text-slate-600">Noms tout en majuscules ({upper.length})</h4>
        {upper.length === 0 && <p className="text-xs text-slate-400">Aucun nom en majuscules.</p>}
        {upper.map((p) => <RenameRow key={p.id} p={p} cfg={cfg} base={base} onDone={onChanged} />)}
      </section>
    </Modal>
  );
}
